import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover } from 'radix-ui';
import { useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, Ref } from 'react';
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  callingCode,
  countryByIso,
  countryForCallingCode,
} from '@sbg/shared/constants';
import type { Country } from '@sbg/shared/constants';
import { composePhone, formatDial, splitPhone } from '@sbg/shared/utils';
import './phone-field.css';

export interface PhoneFieldLabels {
  /** Name of the country-code button: "Country code" (the chosen country and code follow it). */
  country: string;
  /** Name and placeholder of the search box in the list. */
  search: string;
  /** Shown when the search finds nothing. */
  none: string;
}

export interface PhoneFieldProps {
  id: string;
  /** The phone number as the form holds it: `+` and digits (`+919024780751`), or `''`. */
  value: string;
  /** Called with that same shape whenever the country or the typed number changes. */
  onChange: (value: string) => void;
  onBlur?: (() => void) | undefined;
  invalid?: boolean | undefined;
  placeholder?: string | undefined;
  'aria-describedby'?: string | undefined;
  labels: PhoneFieldLabels;
  inputRef?: Ref<HTMLInputElement> | undefined;
  name?: string | undefined;
}

/** What the field shows: the country, the text in the number box, and the number it last gave the form. */
interface Shown {
  country: Country;
  text: string;
  sent: string;
}

const INDIA: Country = countryByIso(DEFAULT_COUNTRY) ?? { iso: DEFAULT_COUNTRY, dial: '91' };

/** The field's view of a number the form holds: split into country and the rest (a number it cannot read stays as typed). */
function showNumber(value: string, current: Country): Shown {
  if (value === '') return { country: current, text: '', sent: '' };
  const split = splitPhone(value);
  return split
    ? { country: split.country, text: split.national, sent: value }
    : { country: current, text: value, sent: value };
}

interface Option {
  iso: string;
  dial: string;
  name: string;
  /** Lower-case, accent-free text the search looks in. */
  haystack: string;
}

/** The language the country names are shown in. */
const NAME_LANGUAGE = 'en';

const fold = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** The country names in English (a browser has them all), India first, the rest in order. */
function buildOptions(): Option[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([NAME_LANGUAGE], { type: 'region' });
  } catch {
    names = null;
  }
  const options = COUNTRIES.map((country) => {
    const name = names?.of(country.iso) ?? country.iso;
    return {
      iso: country.iso,
      dial: country.dial,
      name,
      haystack: `${fold(name)} ${country.iso.toLowerCase()}`,
    };
  });
  const first = options.filter((option) => option.iso === DEFAULT_COUNTRY);
  const rest = options
    .filter((option) => option.iso !== DEFAULT_COUNTRY)
    .sort((a, b) => a.name.localeCompare(b.name, NAME_LANGUAGE));
  return [...first, ...rest];
}

function matches(option: Option, query: string): boolean {
  const q = fold(query.trim());
  if (q === '') return true;
  const digits = q.replace(/\D/g, '');
  if (q.startsWith('+') || /^\d+$/.test(q)) return option.dial.startsWith(digits);
  return option.haystack.includes(q);
}

/**
 * A phone number with its country code: a button that opens a searchable list of every country (name in the
 * visitor's language, ISO code, dial code) beside the number box. The form holds one string, `+` and the digits —
 * what the server validates and stores. Typing a number with its own `+91…` / `0044…` switches the country to
 * match, so a pasted or auto-filled international number lands in the right place; a national `0` or India's `91`
 * is dropped (`composePhone`). Whether the number is a phone number is the shared `normalizePhone`, in the form's
 * schema here and again on the server.
 */
export function PhoneField({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  placeholder,
  labels,
  inputRef,
  name,
  'aria-describedby': describedBy,
}: PhoneFieldProps) {
  // What the field shows is its own state: the country, the typed text, and the number it last handed to the form.
  // When the form's number is not that one, the form changed it by itself (a prefill, a reset): show it instead.
  const [shown, setShown] = useState<Shown>({ country: INDIA, text: '', sent: '' });
  if (value !== shown.sent) setShown(showNumber(value, shown.country));
  const { country, text } = shown;
  const iso = country.iso;
  const dial = country.dial;

  const change = (nextCountry: Country, nextText: string): void => {
    const composed = composePhone(nextCountry.dial, nextText);
    setShown({ country: nextCountry, text: nextText, sent: composed });
    onChange(composed);
  };

  const typed = (raw: string): void => {
    const split = splitPhone(raw);
    if (split) {
      // `+44 …` / `0044 …` names its own country: switch to it (a +1 number keeps a North American country)
      const keepNorthAmerica = split.country.dial === '1' && dial.startsWith('1');
      change(keepNorthAmerica ? country : split.country, split.national);
      return;
    }
    // what is left of "+91-9024780751" after "+91" is taken for the country begins with a dash: not part of the number
    change(country, raw.startsWith('+') ? raw : raw.replace(/^[\s().-]+/, ''));
  };

  const options = useMemo(() => buildOptions(), []);
  const countryName = options.find((option) => option.iso === iso)?.name ?? iso;

  const [open, setOpen] = useState(false);
  const numberRef = useRef<HTMLInputElement | null>(null);
  useImperativeHandle(inputRef, () => numberRef.current as HTMLInputElement, []);
  const picked = useRef(false);

  return (
    <div className="phone-field" data-invalid={invalid ? 'true' : undefined}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          type="button"
          className="field phone-field__country"
          aria-invalid={invalid || undefined}
          aria-label={`${labels.country}: ${countryName} ${formatDial(dial)}`}
        >
          <span className="phone-field__iso" aria-hidden="true">
            {iso}
          </span>
          <span aria-hidden="true">{formatDial(dial)}</span>
          <ChevronDown size={16} aria-hidden="true" className="phone-field__chevron" />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="phone-list"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            data-lenis-prevent=""
            onCloseAutoFocus={(event) => {
              // after choosing, the next thing to do is type the number; Escape goes back to the button
              if (!picked.current) return;
              picked.current = false;
              event.preventDefault();
              numberRef.current?.focus();
            }}
          >
            <CountryList
              options={options}
              selected={iso}
              labels={labels}
              onPick={(option) => {
                picked.current = true;
                change(countryByIso(option.iso) ?? country, text);
                setOpen(false);
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <input
        id={id}
        name={name}
        ref={numberRef}
        className="field phone-field__number"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder={placeholder}
        value={text}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event) => {
          typed(event.target.value);
        }}
        onBlur={onBlur}
      />
    </div>
  );
}

function CountryList({
  options,
  selected,
  labels,
  onPick,
}: {
  options: readonly Option[];
  selected: string;
  labels: PhoneFieldLabels;
  onPick: (option: Option) => void;
}) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const found = options.filter((option) => matches(option, query));
    // searching by code (+44): of the countries that share it, the best known comes first (the United Kingdom, not Guernsey)
    if (!/^[+d]/.test(query.trim())) return found;
    const rank = (option: Option): number =>
      countryForCallingCode(callingCode(option.dial))?.iso === option.iso ? 0 : 1;
    return [...found].sort((a, b) => rank(a) - rank(b));
  }, [options, query]);
  const [active, setActive] = useState(() =>
    Math.max(
      0,
      options.findIndex((o) => o.iso === selected),
    ),
  );
  const list = useRef<HTMLUListElement | null>(null);
  const search = useRef<HTMLInputElement | null>(null);

  // the search box takes the focus the moment the list opens
  useEffect(() => {
    search.current?.focus();
  }, []);

  const current = Math.min(active, Math.max(0, shown.length - 1));
  const optionId = (index: number): string => `${listId}-${String(index)}`;

  // keep the highlighted row in view (inside the list only)
  useEffect(() => {
    const row = list.current?.children[current];
    if (row instanceof HTMLElement) row.scrollIntoView({ block: 'nearest' });
  }, [current, shown]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(Math.min(current + 1, shown.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.max(current - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(shown.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = shown[current];
      if (option) onPick(option);
    }
  };

  return (
    <>
      <div className="phone-list__search">
        <Search size={16} aria-hidden="true" />
        <input
          type="text"
          role="combobox"
          className="phone-list__input"
          aria-label={labels.search}
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={shown.length > 0 ? optionId(current) : undefined}
          placeholder={labels.search}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={query}
          ref={search}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <ul
        ref={list}
        id={listId}
        role="listbox"
        aria-label={labels.search}
        className="phone-list__options"
      >
        {shown.map((option, index) => (
          // keyboard use goes through the search box (aria-activedescendant); the rows are buttons for the mouse and touch
          <li key={option.iso} role="presentation">
            <button
              type="button"
              id={optionId(index)}
              role="option"
              tabIndex={-1}
              aria-selected={option.iso === selected}
              data-active={index === current ? 'true' : undefined}
              className="phone-list__option"
              onPointerMove={() => {
                if (index !== current) setActive(index);
              }}
              onClick={() => {
                onPick(option);
              }}
            >
              <span className="phone-list__name">{option.name}</span>
              <span className="phone-list__dial">{formatDial(option.dial)}</span>
              {option.iso === selected ? (
                <Check size={16} aria-hidden="true" className="phone-list__check" />
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      {shown.length === 0 ? (
        <p className="phone-list__none" role="status">
          {labels.none}
        </p>
      ) : null}
    </>
  );
}
